import {
  createWebinar,
  createWebinarQuestion,
  deleteWebinarQuestion,
  deleteWebinar,
  fetchWebinarCategories,
  fetchAllPastWebinars,
  fetchAllUpcomingWebinars,
  fetchLatestPastWebinars,
  fetchPastWebinarsPaginated,
  fetchUpcomingWebinars,
  fetchWebinarByYouTubeUrl,
  fetchWebinarQuestions,
  fetchWebinar,
  type PaginatedPastWebinarsResult,
  toggleWebinarQuestionLike,
  updateWebinar,
} from '@core/services/webinars/webinars-service'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import { ImageService } from '@core/services/image/image-service'
import {
  getAuthenticatedUidOrThrow,
  loadCurrentUserProfile,
} from '@core/services/user/user-service'
import { isYouTubeShortUrl } from '@domain/features/youtube/youtube-url'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import type {
  CreateWebinarInput,
  UpdateWebinarPatch,
  WebinarCategory,
  Webinar,
  WebinarQuestion,
} from '@domain/models/webinar'

function isUpcoming(webinarDate: Date): boolean {
  return webinarDate.getTime() > Date.now()
}

function isAdminUser(user: { admin?: boolean; isAdmin?: boolean }): boolean {
  return user.admin === true || user.isAdmin === true
}

function assertNoYouTubeShorts(youtubeUrl: string | undefined): void {
  if (!youtubeUrl?.trim()) {
    return
  }

  if (isYouTubeShortUrl(youtubeUrl)) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'YouTube Shorts links are not allowed for webinars. Use a full video URL.',
    })
  }
}

const imageService = ImageService.getInstance()

export class WebinarsFeature {
  public static async getLatestPastWebinars(
    count = 4,
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      return await fetchLatestPastWebinars(count, new Date(), webinarCategoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch latest webinars.',
        underlyingException: error,
      })
    }
  }

  public static async getUpcomingWebinars(
    count = 2,
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      return await fetchUpcomingWebinars(count, new Date(), webinarCategoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch upcoming webinars.',
        underlyingException: error,
      })
    }
  }

  public static async getAllPastWebinars(webinarCategoryId?: string): Promise<Webinar[]> {
    try {
      return await fetchAllPastWebinars(new Date(), webinarCategoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch webinar archive.',
        underlyingException: error,
      })
    }
  }

  public static async getAllUpcomingWebinars(
    webinarCategoryId?: string,
  ): Promise<Webinar[]> {
    try {
      return await fetchAllUpcomingWebinars(new Date(), webinarCategoryId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch upcoming webinars list.',
        underlyingException: error,
      })
    }
  }

  public static async getPastWebinarsPaginated(
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
    webinarCategoryId?: string,
  ): Promise<PaginatedPastWebinarsResult> {
    try {
      return await fetchPastWebinarsPaginated(
        pageSize,
        lastDocument,
        new Date(),
        webinarCategoryId,
      )
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch paginated webinar archive.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarCategories(
    activeOnly = true,
  ): Promise<WebinarCategory[]> {
    try {
      return await fetchWebinarCategories(activeOnly)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch webinar categories.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarByYouTubeUrl(
    youtubeUrl: string,
  ): Promise<Webinar | null> {
    try {
      return await fetchWebinarByYouTubeUrl(youtubeUrl)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to find webinar by YouTube URL.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinar(webinarId: string): Promise<Webinar> {
    try {
      return await fetchWebinar(webinarId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch webinar "${webinarId}".`,
        underlyingException: error,
      })
    }
  }

  public static async createWebinar(input: CreateWebinarInput): Promise<string> {
    try {
      assertNoYouTubeShorts(input.youtubeUrl)
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      return await createWebinar(input, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create webinar.',
        underlyingException: error,
      })
    }
  }

  public static async updateWebinar(
    webinarId: string,
    patch: UpdateWebinarPatch,
  ): Promise<void> {
    try {
      assertNoYouTubeShorts(patch.youtubeUrl)
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await updateWebinar(webinarId, patch, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update webinar "${webinarId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteWebinar(webinarId: string): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await deleteWebinar(webinarId, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete webinar "${webinarId}".`,
        underlyingException: error,
      })
    }
  }

  public static async uploadWebinarCoverImage(file: File): Promise<string> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await imageService.uploadUserImage(uid, file)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to upload webinar cover image.',
        underlyingException: error,
      })
    }
  }

  public static async submitWebinarQuestion(
    webinarId: string,
    question: string,
  ): Promise<string> {
    const normalizedQuestion = question.trim()
    if (normalizedQuestion.length === 0) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Question is required.',
      })
    }

    try {
      const webinar = await fetchWebinar(webinarId)
      if (!isUpcoming(webinar.webinarDate)) {
        throw new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: 'Questions can be submitted only for upcoming webinars.',
        })
      }

      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      return await createWebinarQuestion(
        webinarId,
        { question: normalizedQuestion },
        currentUser,
      )
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to submit question for webinar "${webinarId}".`,
        underlyingException: error,
      })
    }
  }

  public static async getSubmittedWebinarQuestions(
    webinarId: string,
  ): Promise<WebinarQuestion[]> {
    try {
      const currentUserId = AuthenticationService.getInstance().userId ?? undefined
      return await fetchWebinarQuestions(webinarId, currentUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch submitted questions for webinar "${webinarId}".`,
        underlyingException: error,
      })
    }
  }

  public static async toggleSubmittedWebinarQuestionLike(
    webinarId: string,
    questionId: string,
  ): Promise<boolean> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await toggleWebinarQuestionLike(webinarId, questionId, uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to toggle like for webinar question "${questionId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteSubmittedWebinarQuestion(
    webinarId: string,
    questionId: string,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      if (!isAdminUser(currentUser)) {
        throw new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: 'Current user is not allowed to delete webinar questions.',
        })
      }

      await deleteWebinarQuestion(webinarId, questionId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete webinar question "${questionId}".`,
        underlyingException: error,
      })
    }
  }
}

export async function getLatestPastWebinars(
  count = 4,
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return WebinarsFeature.getLatestPastWebinars(count, webinarCategoryId)
}

export async function getUpcomingWebinars(
  count = 2,
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return WebinarsFeature.getUpcomingWebinars(count, webinarCategoryId)
}

export async function getAllPastWebinars(webinarCategoryId?: string): Promise<Webinar[]> {
  return WebinarsFeature.getAllPastWebinars(webinarCategoryId)
}

export async function getAllUpcomingWebinars(
  webinarCategoryId?: string,
): Promise<Webinar[]> {
  return WebinarsFeature.getAllUpcomingWebinars(webinarCategoryId)
}

export async function getPastWebinarsPaginated(
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  webinarCategoryId?: string,
): Promise<PaginatedPastWebinarsResult> {
  return WebinarsFeature.getPastWebinarsPaginated(
    pageSize,
    lastDocument,
    webinarCategoryId,
  )
}

export async function getWebinarCategories(
  activeOnly = true,
): Promise<WebinarCategory[]> {
  return WebinarsFeature.getWebinarCategories(activeOnly)
}

export async function getWebinarByYouTubeUrl(
  youtubeUrl: string,
): Promise<Webinar | null> {
  return WebinarsFeature.getWebinarByYouTubeUrl(youtubeUrl)
}

export async function getWebinar(webinarId: string): Promise<Webinar> {
  return WebinarsFeature.getWebinar(webinarId)
}

export async function createNewWebinar(input: CreateWebinarInput): Promise<string> {
  return WebinarsFeature.createWebinar(input)
}

export async function updateExistingWebinar(
  webinarId: string,
  patch: UpdateWebinarPatch,
): Promise<void> {
  return WebinarsFeature.updateWebinar(webinarId, patch)
}

export async function deleteExistingWebinar(webinarId: string): Promise<void> {
  return WebinarsFeature.deleteWebinar(webinarId)
}

export async function uploadWebinarCoverImage(file: File): Promise<string> {
  return WebinarsFeature.uploadWebinarCoverImage(file)
}

export async function submitWebinarQuestion(
  webinarId: string,
  question: string,
): Promise<string> {
  return WebinarsFeature.submitWebinarQuestion(webinarId, question)
}

export async function getSubmittedWebinarQuestions(
  webinarId: string,
): Promise<WebinarQuestion[]> {
  return WebinarsFeature.getSubmittedWebinarQuestions(webinarId)
}

export async function toggleSubmittedWebinarQuestionLike(
  webinarId: string,
  questionId: string,
): Promise<boolean> {
  return WebinarsFeature.toggleSubmittedWebinarQuestionLike(webinarId, questionId)
}

export async function deleteSubmittedWebinarQuestion(
  webinarId: string,
  questionId: string,
): Promise<void> {
  return WebinarsFeature.deleteSubmittedWebinarQuestion(webinarId, questionId)
}
